import { Link } from "react-router-dom";
import {
    GraduationCap, Stethoscope, Atom, Cog, MessagesSquare, MonitorSmartphone,
    Globe2, Trophy, BookOpen, Sparkles, ArrowRight,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { COURSES, GRADES, BOARDS, SUBJECTS } from "../data/siteData";
import { BOARD_COURSES } from "../data/boardCoursesData";

const ICONS = { GraduationCap, Stethoscope, Atom, Cog, MessagesSquare, MonitorSmartphone, Globe2, Trophy };

export default function Courses() {
    const courseSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": "Anand Tutorials — Courses & Programs",
        "itemListElement": COURSES.map((c, idx) => ({
            "@type": "ListItem",
            "position": idx + 1,
            "item": {
                "@type": "Course",
                "name": c.title,
                "description": c.desc,
                "provider": {
                    "@type": "EducationalOrganization",
                    "name": "Anand Tutorials",
                    "url": "https://anandtutorials.com",
                },
                "hasCourseInstance": {
                    "@type": "CourseInstance",
                    "courseMode": ["onsite", "online"],
                    "courseWorkload": "PT3H",
                },
                "offers": {
                    "@type": "Offer",
                    "category": "Paid",
                    "priceCurrency": "INR",
                    "availability": "https://schema.org/InStock",
                    "url": "https://anandtutorials.com/register",
                },
            },
        })),
    };

    return (
        <div data-testid="courses-page">
            <SEO
                title="Courses & Programs — All Boards, All Grades | Anand Tutorials"
                description="Complete tutoring coverage: Nursery to 12th, CBSE, ICSE, IGCSE, IB, A-Level, AP, NEET, JEE, engineering subjects, spoken English, computer courses, Olympiads. 1:1 home & online."
                keywords="CBSE coaching, ICSE coaching, IGCSE tuition, IB tuition, A-Level tutor, AP tutor, NEET coaching, JEE coaching, engineering tuition, spoken English class, computer course, NTSE, Olympiad coaching"
                path="/courses"
                schema={courseSchema}
            />
            <Breadcrumbs items={[{ name: "Courses" }]} />
            <PageHeader
                eyebrow="Programs & Courses"
                title="Complete academic coverage — Nursery to Engineering."
                subtitle="All boards, all grades, all subjects, all major competitive exams. Choose home tuition or live 1:1 online — taught by verified, hand-picked tutors."
            />

            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Programs</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10">Our flagship offerings</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {COURSES.map((c) => {
                            const Icon = ICONS[c.icon] || GraduationCap;
                            return (
                                <div key={c.title} className="bg-[#F8FAFC] rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200 transition-all">
                                    <div className="w-12 h-12 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center mb-4">
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-heading text-lg font-semibold mb-2">{c.title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed mb-4">{c.desc}</p>
                                    <ul className="space-y-1.5">
                                        {c.bullets.map((b) => (
                                            <li key={b} className="text-xs text-slate-500 flex items-center gap-2">
                                                <span className="w-1 h-1 rounded-full bg-amber-500"></span>{b}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Batch 2Q pre-deploy fix — 12 pillar page cards (was missing;
                previously the /courses hub had ZERO links to the 12 /courses/<slug>
                pillars, orphaning the QuickAnswerCard + Comparison Matrix from
                Batches 2P & 2Q. This block closes the hub → pillar internal-link
                path required for topic-cluster PageRank flow. */}
            <section className="py-16 bg-white border-t border-slate-100" data-testid="courses-pillar-grid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Board & exam-specialist pillars</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Pick a board or exam — see the specialist page.</h2>
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-8 max-w-3xl">
                        Each pillar page opens with a Quick-Answer card, a side-by-side comparison matrix versus its closest alternative, syllabus deep-dive, tutor pedigree, transparent price band and a free-demo booking form.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.values(BOARD_COURSES).map((b) => (
                            <Link
                                key={b.slug}
                                to={`/courses/${b.slug}`}
                                data-testid={`courses-pillar-card-${b.slug}`}
                                className="group flex items-start gap-3 p-5 rounded-2xl bg-[#F8FAFC] border border-slate-200 hover:border-[#1E3A8A] hover:bg-white hover:shadow-sm transition-all"
                            >
                                <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/5 text-[#1E3A8A] flex items-center justify-center shrink-0 group-hover:bg-[#1E3A8A] group-hover:text-white transition-colors">
                                    <ArrowRight className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-heading font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-[#1E3A8A]">
                                        {b.label}
                                    </div>
                                    <div className="text-xs text-slate-500 leading-relaxed line-clamp-2">
                                        {b.eyebrow}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* V83 — Makeover block moved further down (below All Subjects,
                above the closing CTA) so the academic programme flow reads
                Programmes → Grades → Boards → Subjects → Makeover → CTA. */}

            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Grade-wise</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10">Tuition for every age & stage</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {GRADES.map((g) => (
                            <div key={g.range} className="bg-white rounded-xl border border-slate-200 p-6">
                                <div className="flex items-center gap-3 mb-2">
                                    <BookOpen className="w-5 h-5 text-[#1E3A8A]" />
                                    <div className="font-heading font-semibold text-[#1E3A8A]">{g.range}</div>
                                </div>
                                <div className="text-sm text-slate-600 leading-relaxed">{g.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">All Boards</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-10">Specialist tutors for every board.</h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {BOARDS.map((b) => (
                            <div key={b.code} className="bg-[#F8FAFC] rounded-xl border border-slate-200 p-5">
                                <div className="font-heading font-semibold text-[#1E3A8A] mb-1">{b.name}</div>
                                <div className="text-xs text-slate-500 leading-relaxed">{b.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16 bg-[#0F172A] text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-400 mb-3">Subjects</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-8">All subjects covered.</h2>
                    <div className="flex flex-wrap gap-2">
                        {SUBJECTS.map((s) => (
                            <span key={s} className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm">{s}</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* V83 — 1-on-1 Live Online Makeover Classes (moved here from
                its earlier position, now sits between "All Subjects" and the
                closing "Not sure which program fits?" CTA). Content, design,
                testids and SEO surface are untouched — position only. */}
            <section className="py-16 bg-gradient-to-br from-rose-50 via-white to-amber-50 border-y border-rose-100" data-testid="courses-makeover-block">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-rose-600 mb-3 inline-flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> Beyond Academics
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3 text-slate-900">
                        1-on-1 Live Online Makeover Classes
                    </h2>
                    <p className="text-slate-700 text-base leading-relaxed mb-8 max-w-3xl">
                        Beyond board tuition, we also offer premium 1-on-1 live online coaching in personal grooming, styling, image consulting, personal branding and confidence building &mdash; worldwide and Pan India. Delivered by our dedicated expert Ishpreet Kaur over Zoom.
                    </p>
                    <div className="grid lg:grid-cols-5 gap-6 items-start">
                        <div className="lg:col-span-2">
                            <img
                                src="/makeover/ishpreet-kaur.webp"
                                srcSet="/makeover/ishpreet-kaur-400w.webp 400w, /makeover/ishpreet-kaur-640w.webp 640w"
                                sizes="(max-width: 640px) 100vw, 320px"
                                alt="Ishpreet Kaur — 1-on-1 Live Online Makeover Expert at Anand Tutorials"
                                loading="lazy"
                                decoding="async"
                                width={640} height={800}
                                data-testid="courses-makeover-expert-img"
                                className="w-full max-w-sm aspect-[4/5] object-cover rounded-2xl shadow-xl shadow-rose-100 ring-1 ring-rose-100"
                            />
                            <div className="mt-3">
                                <div className="font-heading font-bold text-slate-900 text-lg">Ishpreet Kaur</div>
                                <div className="text-[11px] text-rose-600 font-semibold uppercase tracking-wider">1-on-1 Live Online Makeover Expert</div>
                                <div className="text-xs text-slate-500 mt-0.5">Serving Worldwide + Pan India Clients</div>
                            </div>
                        </div>
                        <div className="lg:col-span-3">
                            <div className="grid sm:grid-cols-2 gap-3 mb-6">
                                {[
                                    { t: "Personal Grooming", d: "Daily routine mastery" },
                                    { t: "Makeup Training", d: "Camera-based live practice" },
                                    { t: "Skincare Guidance", d: "Skin-type calibrated routines" },
                                    { t: "Personal Styling", d: "Body shape + colour season" },
                                    { t: "Wardrobe Consultation", d: "Closet audit + shopping list" },
                                    { t: "Hair Styling Guidance", d: "At-home styling techniques" },
                                    { t: "Image Consulting", d: "Executive presence coaching" },
                                    { t: "Personal Branding", d: "LinkedIn + social identity" },
                                    { t: "Confidence Coaching", d: "Body language + presence drills" },
                                    { t: "Professional Appearance", d: "Interview + client-meeting look" },
                                ].map((s) => (
                                    <div key={s.t} className="rounded-xl bg-white border border-slate-200 p-3 hover:border-rose-300 hover:shadow-sm transition-all">
                                        <div className="font-heading font-bold text-sm text-slate-900">{s.t}</div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">{s.d}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex flex-wrap gap-3">
                                <Link
                                    to="/online-makeover-classes"
                                    data-testid="courses-makeover-cta"
                                    className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white font-bold px-5 py-3 rounded-lg text-sm"
                                >
                                    Explore Makeover Classes <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link
                                    to="/online-makeover-classes#enquire"
                                    data-testid="courses-makeover-enquire"
                                    className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-rose-400 text-slate-800 font-bold px-5 py-3 rounded-lg text-sm"
                                >
                                    Book a 1-on-1 slot
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 bg-white text-center">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Not sure which program fits?</h2>
                    <p className="text-slate-600 mb-6">Talk to our academic counsellor &mdash; we&apos;ll match your child with the right course and tutor in 24 hours.</p>
                    <Link to="/register" data-testid="courses-cta-demo" className="inline-flex items-center bg-[#1E3A8A] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#1E40AF]">
                        Book Free Demo Class
                    </Link>
                </div>
            </section>
        </div>
    );
}
